import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MasterService } from '../../services/master.service'
import { MessageService } from '../../services/message.service'
import { StoreService } from '../../services/store.service'
import * as languageLibrary from '../../services/language'

import { ROLE_TYPES } from 'src/app/constants/user';
import { SecurityService } from 'src/app/services/security.service';
import { Role } from 'src/app/models/role.model';

@Component({
    selector: 'app-security',
    templateUrl: './security.component.html',
    styleUrls: ['./security.component.scss'],
    standalone: false
})
export class SecurityComponent implements OnInit {
    /*
    * Variables
    */

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    // define  rol name selected
    protected roleSelected: any = {}

    // define rol object selected
    protected roleTarget: any = {}

    //  define the roles Array
    protected roles: Role[] = []

    // A list of currently available role types
    protected roleTypes: string[] = ROLE_TYPES

    // define the status loading
    protected loading: boolean = true

    protected information = new FormGroup({
        id: new FormControl<number>(0),
        name: new FormControl<string | null>(null, [Validators.required]),
        type: new FormControl<string | null>(null, [Validators.required])
    })

    get editing() { 
        const id = this.information.get('id').value; 
        return (id && (id > 0)) 
    }

    /*
    * Life cycles
    */

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, protected security: SecurityService) {}

    ngOnInit() {
        this.getRoles(true)
    }

    // toggle function to show or hide the input to add new role
    protected toggleInput = (): void => {
        this.information.reset()
    }

    // select a role
    protected selectRole = (role: Object): void => {
        this.roleSelected = JSON.parse(JSON.stringify(role))
    }

    /* define role is updating */
    protected addRole = (): void => {
        this.information.reset()

        const modal = (document.getElementById('edit-modal-btn') as HTMLButtonElement)
        if (!modal) {
            return
        }

        modal.click()
    }

    /* define role is updating */
    protected editRole = (role: Object): void => {
        this.information.get("id").setValue(parseInt(role['id']))
        this.information.get("name").setValue(role['name'])
        this.information.get("type").setValue(role['type'])

        const modal = (document.getElementById('edit-modal-btn') as HTMLButtonElement)
        if (!modal) {
            return
        }

        modal.click()
    }

    // Ask for confirmation to discard an existing role
    protected discardRole = (role: Object): void => {
        const modal = (document.getElementById('delete-modal-btn') as HTMLButtonElement)
        if (!modal) {
            return
        }

        modal.click()
        this.roleTarget = { ...role };
    }

    // todo: listen to output of navbar
    protected listenerNav = (e): void => {
        switch (e.message) {
            case 'changeLanguage':
                this.language = e.value
                break
            case 'setPermissions':
                this.getRoles(false)
                break
            default:
                break
        }
    }

    /**
     * Add or edit a role.
     * @param {Event} e The event that triggered the save
     */
    protected save = (e: Event): void => {
        e.stopPropagation()
        e.preventDefault()

        const modal = (document.getElementById('edit-modal-btn') as HTMLButtonElement)
        if (modal) {
            modal.click()
        }

        const id = this.information.get('id').value
        if (!id || isNaN(id) || (id <= 0)) {
            this.createRole()
            return
        }
       
        this.modifyRole()
    }

    protected createRole() {
        const payload = {
            name: this.information.get('name').value,
            type: this.information.get('type').value
        }

        this.master.post(`roles`, payload, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            this.getRoles(false)
            this.ms.channelComponents$.emit({ message: 'reloadRoles', section: 'security', value: null })
            this.information.reset()
        })
    }

    protected modifyRole() {
        const id = this.information.get('id').value
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        const payload = {
            name: this.information.get('name').value,
            type: this.information.get('type').value
        }

        this.master.patch(`roles/${id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            this.getRoles(false)
            this.ms.channelComponents$.emit({ message: 'reloadRoles', section: 'security', value: null })
            this.information.reset()
        })
    }

    protected deleteRole() {
        const id = parseInt(this.roleTarget['id'])
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        this.master.discard(`roles/${id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            (document.getElementById('closeModalDelete') as HTMLButtonElement).click()
            this.getRoles(false)
            this.ms.channelComponents$.emit({ message: 'reloadRoles', section: 'security', value: null })
            this.information.reset()
            this.roleSelected = null;
            this.roleTarget = null;
        })
    }

    protected copyRole(role: Object) {
        const id = parseInt(role['id'])
        if (!id || isNaN(id) || (id <= 0)) {
            return
        }

        this.master.post(`roles/${id}/copy`, {}, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            this.getRoles(false)
            this.ms.channelComponents$.emit({ message: 'reloadRoles', section: 'security', value: null })
            this.information.reset()
        })
    }

    protected getRoles(loading) {
        this.loading = loading
        
        this.master.get(`roles`, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            this.roles = res.data.roles
            if (!this.roles || (this.roles.length <= 0)) {
                return
            }

            this.roleSelected = JSON.parse(JSON.stringify(this.roles[0]))
        })
    }

    protected displayUserRoles = (e: Event): void => {
        this.roleSelected = null
    }
}